'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
  TvStreamStatus,
} from '@concept/protocol';

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
type AirPlayVideo = HTMLVideoElement & {
  webkitCurrentPlaybackTargetIsWireless?: boolean;
  webkitShowPlaybackTargetPicker?: () => void;
};

export function BoardSettingsModal({
  currentNickname,
  isHost,
  isOpen,
  onClose,
  onRenameNickname,
  roomCode,
  socket,
}: {
  currentNickname: string;
  isHost: boolean;
  isOpen: boolean;
  onClose: () => void;
  onRenameNickname: (nickname: string) => Promise<string | null>;
  roomCode: string;
  socket: GameSocket | null;
}) {
  const videoRef = useRef<AirPlayVideo>(null);
  const [nicknameDraft, setNicknameDraft] = useState(currentNickname);
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [stream, setStream] = useState<TvStreamStatus | null>(null);
  const [streamError, setStreamError] = useState('');
  const [isWireless, setIsWireless] = useState(false);
  const [streamBusy, setStreamBusy] = useState(false);
  const [streamLinkCopied, setStreamLinkCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNicknameDraft(currentNickname);
      setRenameError('');
    }
  }, [currentNickname, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeWithEscape);
    return () => window.removeEventListener('keydown', closeWithEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!socket || !isHost) return;
    const receiveStatus = (next: TvStreamStatus) => {
      if (next.roomCode !== roomCode) return;
      if (next.state === 'stopped') {
        setStream(null);
        setIsWireless(false);
      } else {
        setStream(next);
      }
      if (next.state === 'error') {
        setStreamError(next.message ?? 'The TV renderer stopped unexpectedly.');
      }
    };
    socket.on('tv:stream-status', receiveStatus);
    socket.emit('tv:status', { roomCode }, (result) => {
      if (result.ok) setStream(result.value.stream);
    });
    return () => { socket.off('tv:stream-status', receiveStatus); };
  }, [isHost, roomCode, socket]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream?.playbackUrl) return;
    if (video.src !== stream.playbackUrl) {
      video.src = stream.playbackUrl;
      video.load();
    }
  }, [stream?.playbackUrl]);

  useEffect(() => {
    if (!streamLinkCopied) return;
    const timer = window.setTimeout(() => setStreamLinkCopied(false), 1_800);
    return () => window.clearTimeout(timer);
  }, [streamLinkCopied]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const routeChanged = () => {
      const wireless = Boolean(video.webkitCurrentPlaybackTargetIsWireless);
      setIsWireless(wireless);
      if (!socket || !stream) return;
      socket.emit('tv:route-state', {
        roomCode,
        streamId: stream.streamId,
        isWireless: wireless,
      }, () => undefined);
      if (wireless) onClose();
    };
    video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', routeChanged);
    return () => video.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', routeChanged);
  }, [onClose, roomCode, socket, stream]);

  async function renameNickname(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextNickname = nicknameDraft.trim();
    if (!nextNickname || nextNickname === currentNickname || renameBusy) return;
    setRenameBusy(true);
    setRenameError('');
    const error = await onRenameNickname(nextNickname);
    setRenameBusy(false);
    if (error) setRenameError(error);
  }

  function prepare() {
    if (!socket || streamBusy) return;
    setStreamBusy(true);
    setStreamError('');
    socket.emit('tv:prepare', { roomCode }, (result) => {
      setStreamBusy(false);
      if (result.ok) setStream(result.value.stream);
      else setStreamError(result.error.message);
    });
  }

  async function chooseTv() {
    const video = videoRef.current;
    if (!video || !stream?.playbackUrl) return;
    setStreamError('');
    try {
      await video.play();
      if (!video.webkitShowPlaybackTargetPicker) {
        throw new Error('AirPlay controls are not available in this browser.');
      }
      video.webkitShowPlaybackTargetPicker();
    } catch (cause) {
      setStreamError(cause instanceof Error ? cause.message : 'The browser could not start the TV stream.');
    }
  }

  async function copyStreamLink() {
    if (!stream?.playbackUrl) return;
    setStreamError('');
    try {
      await navigator.clipboard.writeText(stream.playbackUrl);
      setStreamLinkCopied(true);
    } catch {
      setStreamLinkCopied(false);
      setStreamError('Could not copy the livestream link.');
    }
  }

  function stop() {
    if (!socket || !stream || streamBusy) return;
    setStreamBusy(true);
    socket.emit('tv:stop', { roomCode, streamId: stream.streamId }, (result) => {
      setStreamBusy(false);
      if (!result.ok) return setStreamError(result.error.message);
      videoRef.current?.pause();
      setStream(null);
      setIsWireless(false);
    });
  }

  const ready = stream?.state === 'ready' && Boolean(stream.playbackUrl);
  return (
    <>
      {isHost && (
        <video
          {...{ 'x-webkit-airplay': 'allow' }}
          aria-hidden="true"
          className="airplay-media"
          playsInline
          preload="auto"
          ref={videoRef}
        />
      )}
      {isOpen && (
        <div className="tv-control-backdrop" onClick={(event) => {
          if (event.currentTarget === event.target) onClose();
        }} role="presentation">
          <section aria-label="Board settings" aria-modal="true" className="tv-control-sheet board-settings-sheet" role="dialog">
            <header>
              <div><strong>Settings</strong><span>Room {roomCode}</span></div>
              <button aria-label="Close settings" onClick={onClose} type="button">×</button>
            </header>

            <section className="board-settings-section">
              <header><strong>Profile</strong><span>How other players see you</span></header>
              <form className="settings-nickname-form" onSubmit={(event) => void renameNickname(event)}>
                <label htmlFor="settings-nickname">Nickname</label>
                <div>
                  <input
                    autoComplete="nickname"
                    id="settings-nickname"
                    maxLength={20}
                    onChange={(event) => setNicknameDraft(event.target.value)}
                    value={nicknameDraft}
                  />
                  <button
                    className="primary"
                    disabled={renameBusy || !nicknameDraft.trim() || nicknameDraft.trim() === currentNickname}
                    type="submit"
                  >
                    {renameBusy ? 'Saving…' : 'Rename'}
                  </button>
                </div>
                {renameError && <p className="error">{renameError}</p>}
              </form>
            </section>

            {isHost && (
              <section className="board-settings-section tv-settings-section">
                <header>
                  <strong>TV display</strong>
                  <span>{isWireless ? 'AirPlay active' : 'Host controls'}</span>
                </header>
                <div className="tv-stream-state">
                  <strong>{stream ? stream.state === 'ready' ? 'Stream ready' : 'Preparing display…' : 'Not prepared'}</strong>
                  <span>{ready ? 'Choose an AirPlay television to begin.' : 'The phone board remains fully interactive while streaming.'}</span>
                </div>
                {streamError && <p className="error">{streamError}</p>}
                <div className="tv-control-actions">
                  {!stream && <button className="primary" disabled={streamBusy} onClick={prepare} type="button">Prepare TV stream</button>}
                  {stream && !ready && <button disabled type="button">Preparing…</button>}
                  {ready && <button className="primary" onClick={() => void chooseTv()} type="button">Choose TV</button>}
                  {ready && (
                    <button
                      aria-label={streamLinkCopied ? 'Livestream link copied' : 'Copy livestream link'}
                      className="tv-copy-stream-link"
                      onClick={() => void copyStreamLink()}
                      title={streamLinkCopied ? 'Livestream link copied' : 'Copy livestream link'}
                      type="button"
                    >
                      {streamLinkCopied ? (
                        <span aria-hidden="true">&#10003;</span>
                      ) : (
                        <svg aria-hidden="true" viewBox="0 0 24 24">
                          <rect height="12" rx="2" width="12" x="8" y="8" />
                          <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
                        </svg>
                      )}
                    </button>
                  )}
                  {stream && <button disabled={streamBusy} onClick={stop} type="button">Stop stream</button>}
                </div>
              </section>
            )}
          </section>
        </div>
      )}
    </>
  );
}
